import {T} from '../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import clsx from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import appConfiguration from '../../config/default.json';
import ClubPoints from '../../assets/icons/bg-club-points 1.png';
import Cash from '../../assets/icons/cash.png';
import KeksPayLogo from '../../assets/icons/kekspay_logo.svg';
import Mastercard from '../../assets/icons/mastercard.png';
import Rectangle from '../../assets/icons/Rectangle.png';
import { GET_BACKEND_CONFIG } from '../../commonQueries';
import loadable from '../../components/loadable';
import { FETCH_CONFIG } from '../../components/Product/ProductSidebarClub/ProductSidebarClub';
import ScreenLoader from '../../components/ScreenLoader/ScreenLoader';
import { useCartContext } from '../../context/CartContext';
import { usePopup } from '../../context/PopupContext';
import { useUserContext } from '../../context/UserContext';
import { useQuarticonContext } from '../../context/QuarticonContext';
import { sendTransactionQuarticon } from '../../hooks/useQuarticonHooks';
import { useNewMonriPayment } from '../../hooks/useNewMonriPayment';
import { checkoutOptionEvent, purchaseEvent, addPaymentInfoEvent } from '../../hooks/useAnalytics';
import usePlaceOrder from '../../hooks/usePlaceOrder';
import useSetOrderNote from '../../hooks/useSetOrderNote';
import useSetPaymentMethod from '../../hooks/useSetPaymentMethod';
import { CHECK_STOCK_STATUS } from '../../queries/cart';
import { GET_CARD } from '../../queries/customer';
import readConfig from '../../utils/readClientConfiguration';
import { useReservedOrderId } from '../../hooks/useReserveOrderId';
import calculateReferenceNumber from '../../utils/calculateReferenceNumber';
import resolveOrderNumberRs from '../../utils/resolveOrderNumberRs';
import PaymentMethodMonri from './PaymentMethodMonri/PaymentMethodMonri';
import PlaceOrderWithTerms from './PlaceOrderWithTerms/PlaceOrderWithTerms';
import './CheckoutPaymentMethods.scss';

const countryCode = readConfig(appConfiguration, 'countryCode');
const monriAuthenticityToken = readConfig(appConfiguration, 'monriAuthenticityToken');
const monriBaseUrl = readConfig(appConfiguration, 'monriBaseUrl');
const monriCheckoutType = readConfig(appConfiguration, 'monriCheckoutType');
const referenceModel = readConfig(appConfiguration, 'referenceModel');
const currencyCode = readConfig(appConfiguration, 'currencyCode');
const Helmet = loadable(() => import('react-helmet-async').then(module => ({ default: module.Helmet })));

const PAYMENT_METHOD_CODE_MONRI = 'pikpay';
const PAYMENT_METHOD_CODE_BANK_TRANSFER = 'banktransfer';
const PAYMENT_METHOD_CODE_CASH_ON_DELIVERY = 'cashondelivery';
const PAYMENT_METHOD_CODE_KEKS_PAY = 'kekspay';

const methodImageSrc = {
  [PAYMENT_METHOD_CODE_KEKS_PAY]: KeksPayLogo,
  [PAYMENT_METHOD_CODE_MONRI]: Mastercard,
  [PAYMENT_METHOD_CODE_BANK_TRANSFER]: Rectangle,
  [PAYMENT_METHOD_CODE_CASH_ON_DELIVERY]: Cash,
};

const getMonriMetadata = (result, cart, numberOfInstallments) => {
  return {
    acquirer: result.transaction_response.acquirer,
    amount: result.amount.toString(),
    approval_code: result.transaction_response.approval_code,
    authentication: result.transaction_response.authentication,
    cc_type: result.transaction_response.cc_type,
    ch_full_name: `${cart.billing_address.firstname} ${cart.billing_address.lastname}`,
    currency: result.currency,
    eci: result.transaction_response.eci,
    enrollment: result.transaction_response.enrollment,
    issuer: result.transaction_response.issuer,
    language: 'hr', // TODO: configure this
    masked_pan: '',
    number_of_installments: numberOfInstallments,
    order_number: result.order_number,
    reference_number: result.transaction_response.reference_number,
    response_code: result.transaction_response.response_code,
    digest: '',
  };
};

const CheckoutPaymentMethods = () => {
  const { t } = useTranslation();
  const { setPaymentMethod, loading: setMethodLoading } = useSetPaymentMethod();
  const { showAddToCartPopupMessage } = usePopup();
  const [paymentMethodCode, setPaymentMethodCode] = useState(null);
  const [monriCardDataValid, setIsMonriCardDataValid] = useState(false);
  const [monriCVValid, setIsmonriCVValid] = useState(false);
  const [monriExpirationDateValid, setIsMonriExpirationDateValid] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const { setOrderNote } = useSetOrderNote();
  const { cart, setOrderNote: clearOrderNote, orderNote, loading: cartLoading , generateReservedId} = useCartContext();
  const grandTotal = (cart && cart.prices.grand_total.value) || 0;
  const { data: config } = useQuery(FETCH_CONFIG);
  const rate = config && config.backendConfig.shop.loyaltyConversion;
  const loyaltyPoints = rate && (Math.floor(grandTotal / rate) || 0);
  const loyaltyPointsBanking = loyaltyPoints && loyaltyPoints * 2;
  const [generatePaymentData] = useNewMonriPayment();
  const { user } = useUserContext();
  const { tracker } = useQuarticonContext();
  const { placeOrder } = usePlaceOrder();
  const reserveOrderId = useReservedOrderId();

  const navigate = useNavigate();
  const client = useApolloClient();

  const { data: backendConfig } = useQuery(GET_BACKEND_CONFIG, {
    fetchPolicy: 'cache-first',
  });

  const { data: loyaltyCard } = useQuery(GET_CARD, {
    fetchPolicy: 'no-cache',
    skip: !user,
  });

  const { success } = (loyaltyCard && loyaltyCard.historyCustomerLoyaltyCard) || {};

  const monriInit = async () => {
    const reserved_order_id = await generateReservedId();

    const paymentData = await generatePaymentData(
      Math.round(cart.prices.grand_total.value * 100), // JS rounding issue
      cart.cart_id.toString(),
        reserved_order_id,
    );
    console.log('bzz paymentData', paymentData)
    // Disabled eslint because Monri comes from external script
    // eslint-disable-next-line no-undef
    const monri = new Monri(monriAuthenticityToken, {
      locale: countryCode.substring(0, 2),
    });

    const components = monri.components({
      clientSecret: paymentData.data.newMonriPayment.clientSecret,
    });

    const card = components.create('card', {
      showInstallmentsSelection: true,
    });

    card.mount('monri-payment-form');

    const monriForm = document.getElementById('monri-payment-form');

    // Check if attribute 'data-initialized' exists on form to avoid double registration of event listener
    if (monriForm.getAttribute('data-initialized') !== 'true') {
      monriForm.addEventListener('submit', async submitEvent => {
        if (cart.billing_address === null) {
          // This is needed in case card form is shown before customer sets their billing address
          return;
        }

        monriForm.setAttribute('data-initialized', 'true');
        submitEvent.preventDefault();

        setPlacingOrder(true);

        try {
          const skus = {};
          const names = {};

          cart.items.forEach(item => {
            skus[item.product.sku] = item.quantity;
            names[item.product.sku] = item.product.name;
          });

          let {
            data: { checkStock },
          } = await client.query({
            query: CHECK_STOCK_STATUS,
            fetchPolicy: 'no-cache',
            variables: { skus: Object.keys(skus) },
          });

          let skuError = false;

          if (checkStock) {
            checkStock = Array.from(checkStock.reduce(
                (m, {itemID, availQTY}) => m.set(itemID, (m.get(itemID) || 0) + availQTY), new Map
            ), ([itemID, availQTY]) => ({itemID, availQTY}));

            checkStock.forEach(stockInfo => {
              Object.keys(skus).forEach(sku => {
                if (stockInfo.itemID === sku && skus[sku] > stockInfo.availQTY) {
                  const message = t('messagePopup.orderQtyError').replace('[product]', names[sku]);
                  showAddToCartPopupMessage(message, 'error', 4000);
                  skuError = true;
                }
              });
            });
          }

          if (skuError === true) {
            return false;
          }

          const transactionParams = {
            address: cart.billing_address.street.join(' '),
            fullName: `${cart.billing_address.firstname} ${cart.billing_address.lastname}`,
            city: cart.billing_address.city,
            zip: cart.billing_address.postcode,
            phone: cart.billing_address.telephone,
            country: cart.billing_address.country.code,
            email: cart.email,
            orderInfo: cart.reserved_order_id,
          };
          console.log('BZZ MONRI SEND', transactionParams)
          let result = await monri.confirmPayment(card, transactionParams);
          
          // DEBUG: Logowanie szczegółów odpowiedzi z Monri
          console.log('Monri Response:', JSON.stringify(result, null, 2));
          // Możesz też wyświetlić informacje w popupie dla szybkiego debugowania
          if (result.result) {
            console.log('Transaction ID:', result.result.id);
            console.log('Status:', result.result.status);
            console.log('Transaction Response:', result.result.transaction_response);
          }

          if (result.error) {
            showAddToCartPopupMessage(result.error.message, 'error', 4000);
            console.error('Monri Error:', result.error);
            return false;
          }

          if (result.result && result.result.status === 'declined') {
            showAddToCartPopupMessage(t('checkout.cardDeclined'), 'error', 4000);
            console.error('Monri Declined:', result.result);
            return false;
          }

          if (orderNote) {
            await setOrderNote({
              note: orderNote,
              email: cart.email,
              method: PAYMENT_METHOD_CODE_MONRI, // TODO: read this from stored / loaded cart object
            });
          }

          // DEBUG: Logowanie metadanych płatności
          const monriMetadata = getMonriMetadata(result.result, cart, monriForm.getAttribute('cc-installments'));
          console.log('Monri Metadata:', JSON.stringify(monriMetadata, null, 2));
          
          const { data: orderData } = await placeOrder({
            additionalData: JSON.stringify(monriMetadata),
          });

          clearOrderNote('');
          if (orderData.placeOrder && orderData.placeOrder.order.order_number) {
            // Calculate reference number for bank transfer
            const referenceNumber = calculateReferenceNumber(orderData.placeOrder.order.frontend_id, '');
            const rsNumber = resolveOrderNumberRs(referenceNumber, cart, referenceModel);
            // eslint-disable-next-line no-restricted-globals
            navigate('/checkout/success', {
              state: {
                orderNumber: orderData.placeOrder.order.frontend_id,
                reference: {
                  number: PAYMENT_METHOD_CODE_MONRI === cart.selected_payment_method.code ? null : referenceNumber,
                  model: referenceModel,
                  rsNumber: rsNumber
                },
                paymentMethod: cart.selected_payment_method.code,
                loyaltyPoints:
                    cart.selected_payment_method.code === PAYMENT_METHOD_CODE_MONRI ? loyaltyPoints : loyaltyPointsBanking,
                email: cart.email,
                isLoyaltyMember: success,
                isLoggedIn: user,
                cart: cart,
                currencyCode: currencyCode
              }
            });

            // Send analytics events
            const orderId = orderData.placeOrder.order.order_number;
            const copyCart = JSON.parse(JSON.stringify(cart));
            copyCart.reserved_order_id = orderId;

            purchaseEvent(copyCart, orderId, backendConfig);
            sendTransactionQuarticon(copyCart, tracker);
          }
        } catch (err) {
          if (err.message.includes('maximum qty')) {
            const productRegex = /for product (.+) is \d+ !$/i;
            const product = productRegex.exec(err.message)[1];
            const message = t('messagePopup.orderQtyError').replace('[product]', product);
            showAddToCartPopupMessage(message, 'error', 4000);
          } else {
            showAddToCartPopupMessage(err.message, 'error', 4000);
          }
        } finally {
          setPlacingOrder(false);
        }
      });
    }

    card.onChange(cardEvent => {
      if (cardEvent.error) {
        showAddToCartPopupMessage(cardEvent.error.message, 'error', 4000);
      }
    });

    card.addChangeListener('card_number', ccEvent => {
      setIsMonriCardDataValid(!!ccEvent.data.brand);
    });

    card.addChangeListener('expiry_date', edEvent => {
      setIsMonriExpirationDateValid(edEvent.data !== null);
    });

    card.addChangeListener('cvv', cvvEvent => {
      setIsmonriCVValid(cvvEvent.valid);
    });

    card.addChangeListener('installments', installmentsEvent => {
      monriForm.setAttribute(
        'cc-installments',
        installmentsEvent.data ? installmentsEvent.data.selectedInstallment : 1,
      );
    });
  };

  /** @type {React.FormEventHandler<HTMLFormElement>} */
  const onChangeHandler = async event => {
    const code = event.currentTarget.value;

    switch (code) {
      case PAYMENT_METHOD_CODE_MONRI:
        //checkoutOptionEvent(3, 'Card Payment');
        addPaymentInfoEvent(cart.items, 'Card Payment')

        break;
      case PAYMENT_METHOD_CODE_BANK_TRANSFER:
        //checkoutOptionEvent(3, 'E-banking Payment');
        addPaymentInfoEvent(cart.items, 'E-banking Payment')

        break;
      case PAYMENT_METHOD_CODE_CASH_ON_DELIVERY:
        //checkoutOptionEvent(3, 'Cash on Delivery');
        addPaymentInfoEvent(cart.items, 'Cash on Delivery')

        break;
      case PAYMENT_METHOD_CODE_KEKS_PAY:
        //checkoutOptionEvent(3, 'Keks Pay');
        addPaymentInfoEvent(cart.items, 'Keks Pay')
        break;
      default:
        break;
    }

    setPaymentMethodCode(null);
    await setPaymentMethod(code);
    setPaymentMethodCode(code);
  };

  useEffect(() => {
    if (!cartLoading && paymentMethodCode === PAYMENT_METHOD_CODE_MONRI && monriCheckoutType === 'components') {
      monriInit();
    }
  }, [paymentMethodCode, grandTotal, cartLoading]);

  const cardDataValid = monriCardDataValid && monriCVValid && monriExpirationDateValid;

  return (
    <div className="checkout-payment-methods">
      {monriCheckoutType === 'components' && (
        <Helmet
          defaultTitle="Emmezeta webshop - sigurna online trgovina"
          titleTemplate="%s | Emmezeta webshop - sigurna online trgovina"
        >
          <script src={`${monriBaseUrl}dist/components.js`} type="text/javascript" />
        </Helmet>
      )}

      {setMethodLoading && <ScreenLoader boxLoader />}

      <div className="checkout-payment-methods__title">
        <T id="orders.paymentMethod" />
      </div>

      {cart &&
        cart.available_payment_methods &&
        cart.available_payment_methods.map(paymentMethod => (
          <label
            key={paymentMethod.code}
            className={clsx('checkout-payment-methods__method', setMethodLoading && 'disabled')}
            htmlFor={`payment-method-${paymentMethod.code}`}
          >
            <div className="checkout-payment-methods__method__desc">
              <div className="payment-method-input-wrapper">
                <input
                  type="radio"
                  id={`payment-method-${paymentMethod.code}`}
                  name={`payment-method-${paymentMethod.code}`}
                  value={paymentMethod.code}
                  checked={paymentMethodCode === paymentMethod.code}
                  onChange={onChangeHandler}
                  disabled={setMethodLoading}
                />
              </div>

              <div className="checkout-payment-methods__method__desc-1">
                <div>
                  <img src={methodImageSrc[paymentMethod.code]} alt={paymentMethod.title} />
                </div>

                <span className="grow">{paymentMethod.title}</span>
              </div>
            </div>

            <div className="checkout-payment-methods__method__desc-2">
              <div className="checkout-payment-methods__method__card">
                <span className="checkout-payment-methods__method__card-number">
                  {paymentMethod.code === PAYMENT_METHOD_CODE_BANK_TRANSFER ||
                  paymentMethod.code === PAYMENT_METHOD_CODE_CASH_ON_DELIVERY
                    ? loyaltyPointsBanking
                    : loyaltyPoints}
                </span>
                <img src={ClubPoints} alt="ClubPoints" />
              </div>

              <span className="checkout-payment-methods__method__points">
                <T id="orders.paymentMethodPointsText" />
              </span>
            </div>

            {paymentMethodCode === paymentMethod.code &&
              (paymentMethodCode === PAYMENT_METHOD_CODE_MONRI && monriCheckoutType === 'components' ? (
                !cartLoading && <PaymentMethodMonri isOrderCreating={!cardDataValid || placingOrder} />
              ) : (
                <PlaceOrderWithTerms />
              ))}
          </label>
        ))}
    </div>
  );
};

export default CheckoutPaymentMethods;
